import { useQuery } from '@tanstack/react-query';
import { portalNavFor, type PortalViewerNav } from '../../../lib/portalNav';
import { isPortalStrategyNav } from '../../../lib/portalStrategyNav';
import {
  fetchPortalInspiration,
  fetchPortalLeadBotClubs,
  fetchPortalLibrary,
  fetchPortalMe,
} from './portalApi';
import { hasAnyKyckConnectService } from '../../../lib/kyckConnect/services';
import { fetchPortalKcClubs } from './kyckConnect/portalSetupApi';
import { fetchPortalStaffClubs } from './speedToLead/portalApi';

// CLIENT PORTAL — THIS VIEWER'S NAV (Round 6). The one place the browser puts
// the two inputs together; the rule itself is `portalNavFor`.
//
//   - `strategyNav` comes from `/api/portal/me`, resolved SERVER-SIDE from the
//     scope. ⚠️ The browser is never handed per-account industries to decide it.
//   - `hasCustomReports` comes from the viewer's own Custom reports tiles — the
//     SAME list endpoint and the SAME react-query key the Custom reports page
//     reads, so the row appears exactly when that page would show something, and
//     the visibility decision stays the server's one function.
//
// ⚠️ Both queries share keys with the rest of the portal, so this costs `/me`
// (already fetched by every page) plus one list request.
//
// Null until `/me` has answered: a nav built on a guessed strategy shape would
// flash the wrong sections. A Custom reports fetch that fails or is pending
// counts as "none" — a missing optional row is the safe way to be wrong.

export function usePortalViewerNav(): PortalViewerNav | null {
  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });
  const custom = useQuery({
    queryKey: ['portal-library-tiles', 'reports.custom'],
    queryFn: () => fetchPortalLibrary('reports.custom'),
    retry: false,
    enabled: Boolean(me.data),
  });
  // ⚠️ Inspiration appears in the nav only when the viewer has a card: the SAME list endpoint and query key the
  // Inspiration page reads, so the row appears exactly when that page would show something. A failed or pending
  // fetch counts as "none" - a missing optional row is the safe way to be wrong.
  const inspiration = useQuery({
    queryKey: ['portal-inspiration-cards'],
    queryFn: fetchPortalInspiration,
    retry: false,
    enabled: Boolean(me.data),
  });
  // ⚠️ Lead conversations appears only when the viewer's scope has at least one bot: the SAME list
  // endpoint and query key the page reads. A failed or pending fetch counts as "none".
  const leadBot = useQuery({
    queryKey: ['portal-lead-bot-clubs'],
    queryFn: fetchPortalLeadBotClubs,
    retry: false,
    enabled: Boolean(me.data),
  });
  // ⚠️ Who we call appears only when one of the viewer's clubs has Speed to Lead set up (`usable`, decided by
  // the server): the SAME list and query key the page reads. A failed or pending fetch counts as "none".
  const whoWeCall = useQuery({
    queryKey: ['portal-staff-clubs'],
    queryFn: fetchPortalStaffClubs,
    retry: false,
    enabled: Boolean(me.data),
  });
  // ⚠️ KyckConnect appears when ANY of the viewer's clubs has any service, the lead relay included (a relay-only club has no bot and
  // no calls, so the two checks above miss it). The SAME list and query key the Dashboard and Setup read.
  const kc = useQuery({ queryKey: ['portal-kc', 'clubs'], queryFn: fetchPortalKcClubs, retry: false, enabled: Boolean(me.data) });
  if (!me.data || !isPortalStrategyNav(me.data.strategyNav)) return null;
  return portalNavFor({
    strategyNav: me.data.strategyNav,
    hasCustomReports: (custom.data?.tiles.length ?? 0) > 0,
    hasSocial: me.data.permissions.includes('social'),
    hasInspiration: (inspiration.data?.cards.length ?? 0) > 0,
    hasLeadConversations: (leadBot.data?.clubs.length ?? 0) > 0,
    hasWhoWeCall: (whoWeCall.data?.clubs ?? []).some((c) => c.usable),
    hasKyckConnect: (kc.data?.clubs ?? []).some((c) => hasAnyKyckConnectService(c.services)),
    // The SAME list and key as above: a club "has AI SMS" by the one service definition, never by a second check here.
    hasAiSms: (kc.data?.clubs ?? []).some((c) => c.services.aiSms.on),
  });
}
